import { useId } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import type { ArtifactsRead } from '../drawerTabs';
import type { RunLabel } from '../runLabel';
import { forgeLinks } from '../forgeLinks';
import { ArtifactEntry } from './ArtifactEntry';

/**
 * "Created by this task": every merge request, pull request and branch the
 * task's runs created, each once, above the runs (spec 119 FR-001, FR-002).
 *
 * Its own states, independent of the runs below it: a slow read shows a
 * placeholder after the usual delay, a failed one says so here with a retry,
 * and neither holds the runs back (FR-015, FR-016). An empty list draws
 * nothing at all — no heading over nothing.
 */
export function CreatedByTask({
  read,
  retry,
  runLabels,
}: {
  read: ArtifactsRead;
  retry: () => void;
  runLabels: ReadonlyMap<string, RunLabel>;
}) {
  const headingId = useId();
  const showPlaceholder = usePendingDisplay(read === 'pending');

  if (read === 'pending') {
    return showPlaceholder ? (
      <div
        data-testid="artifacts-placeholder"
        aria-hidden="true"
        className="flex flex-col gap-2"
      >
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    ) : null;
  }

  if (read === 'failed') {
    return (
      <div
        className="flex flex-col items-start gap-2"
        data-testid="artifacts-state"
      >
        <p className="text-muted-foreground text-sm">
          What this task created could not be loaded.
        </p>
        <Button variant="outline" size="sm" onClick={retry}>
          Try again
        </Button>
      </div>
    );
  }

  const artifacts = read.artifacts ?? [];
  if (artifacts.length === 0) return null;
  const links = forgeLinks(artifacts);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <h3 id={headingId} className="text-foreground text-sm font-medium">
        Created by this task
      </h3>
      <ul aria-label="Created by this task" className="flex flex-col gap-3">
        {artifacts.map((artifact) => (
          <ArtifactEntry
            key={artifact.id}
            artifact={artifact}
            runLabels={runLabels}
            links={links}
          />
        ))}
      </ul>
    </section>
  );
}
